// @skade/dugnad — frivillighetstimer for Heimevernet.
//
// FORMÅL: registrere og dokumentere frivillig innsats — det danske Hjemmeværnet
// har dette som en kjernefunksjon, og det mangler på norsk side. Modulen dekker
// BÅDE utviklingsarbeid (avledet av git-økter) og all annen frivillig innsats
// (øvelse, kurs, vakt, møte, egentrening).
//
// PERSONVERN ER DESIGNET INN, IKKE PÅSATT:
// Frivillighetstimer knyttet til navngitte HV-medlemmer er persondata, og
// aggregert deltakelse avslører styrkestruktur. Derfor:
//  · `personId` er et PSEUDONYM (ikke navn, ikke fødselsnummer, ikke Matrix-id).
//  · Klartekstnavn er VALGFRITT og skal bare settes når den enkelte samtykker.
//  · Modulen har ingen kobling til LODUR eller annen gradert personelldata, og
//    skal ikke ligge i samme enklave.
// Se `personvern.ts` for det som håndheves i kode.
import { z } from 'zod';
/** Hva timene gjelder. «utvikling» kan avledes maskinelt; resten meldes manuelt. */
export const AktivitetstypeSchema = z.enum([
    'utvikling', // programvare/system — kan importeres fra git
    'ovelse', // øvelse og trening i avdeling
    'kurs', // kurs og utdanning
    'vakt', // vakt- og sikringsoppdrag
    'mote', // møte, planlegging, stabsarbeid
    'egentrening', // fysisk trening og egenferdighet
    'materiell', // vedlikehold, kontroll, materiellforvaltning
    'annet',
]);
export const AKTIVITET_NAVN = {
    utvikling: 'Utvikling (system/programvare)',
    ovelse: 'Øvelse og trening',
    kurs: 'Kurs og utdanning',
    vakt: 'Vakt- og sikringsoppdrag',
    mote: 'Møte / stabsarbeid',
    egentrening: 'Egentrening',
    materiell: 'Materiell og vedlikehold',
    annet: 'Annet',
};
/**
 * Hvor timene kommer fra. Skillet er viktig for etterprøvbarhet: en git-avledet
 * økt kan kontrolleres mot commits, en manuell kan ikke. Det skal aldri se ut som
 * om et anslag er en måling.
 */
export const KildeSchema = z.enum(['git', 'manuell', 'import']);
/** Godkjenningsløp. HV-timer skal attesteres av noen andre enn den som melder. */
export const StatusSchema = z.enum(['utkast', 'meldt', 'godkjent', 'avvist']);
export const OktSchema = z.object({
    id: z.string().min(1),
    /** Pseudonym for personen. ALDRI navn eller fødselsnummer. */
    personId: z.string().min(1),
    type: AktivitetstypeSchema,
    /** Start i UTC-millisekunder. */
    startTsMs: z.number().int(),
    /** Varighet i minutter. Lagres som minutter, ikke desimaltimer — avrunding
     * skal skje ved VISNING, ikke i lagret data. */
    minutter: z.number().int().min(1).max(24 * 60),
    kilde: KildeSchema,
    status: StatusSchema,
    /** Kort beskrivelse av hva som ble gjort. */
    hva: z.string().max(500).default(''),
    /** Prosjekt/bølge/sprint timene hører til (fritt, f.eks. «Bølge 141»). */
    bolge: z.string().max(120).optional(),
    /**
     * Oppdrags-/aktivitetskode timene føres på, hvis HV bruker slike i tillegg til
     * personkoden. Ikke persondata i seg selv, så den kan vises i sammenligning.
     * Format er ikke låst — vi kjenner ikke kodeverket ennå.
     */
    oppdragskode: z.string().max(40).optional(),
    /** Sporbarhet for git-avledede økter: repo + commit-sha-er. */
    commits: z.array(z.string().min(7).max(64)).max(500).optional(),
    repo: z.string().max(200).optional(),
    /** Attestert av (pseudonym). Settes ved godkjenning. */
    attestertAv: z.string().optional(),
    attestertTsMs: z.number().int().optional(),
    /** Når raden ble opprettet/endret — revisjonsspor. */
    opprettetTsMs: z.number().int(),
    endretTsMs: z.number().int().optional(),
});
/**
 * ANSATTNUMMER — den koden HV faktisk fører timer på.
 *
 * Dette er en PERSONIDENTIFIKATOR i organisasjonens eget system, ikke et
 * pseudonym. Den er nødvendig for at timene skal kunne føres videre til HV, men
 * den er derfor også det mest sensitive feltet i modulen. Regler som håndheves:
 *  · Aldri med i `offentligPerson`/`offentligOkt` — andre skal se innsats, ikke
 *    hvem sin kode den tilhører.
 *  · Aldri lik `personId` (pseudonymet), ellers er pseudonymiseringen verdiløs.
 *  · Maskeres ved visning (`maskerAnsattnummer`).
 *  · Skal legges inn i drift, ALDRI i kildekode eller commit-melding.
 *
 * Format: vi har foreløpig bare ett eksempel (6 siffer), så skjemaet tillater
 * 4-10 siffer framfor å låse en form vi ikke har grunnlag for. Bekreftes med
 * eier før dette brukes mot HVs system.
 */
export const AnsattnummerSchema = z.string().regex(/^\d{4,10}$/, 'ansattnummer skal være 4-10 siffer');
export const PersonSchema = z.object({
    /** Pseudonym. Brukes overalt i API og UI. */
    id: z.string().min(1),
    /** Offisiell ansattnummer. Se AnsattnummerSchema — behandles som persondata. */
    ansattnummer: AnsattnummerSchema.optional(),
    /** Valgfritt klartekstnavn — settes KUN ved samtykke. */
    navn: z.string().max(120).optional(),
    /** Grov tilhørighet (distrikt/område). Ikke lag/tropp — det er styrkestruktur. */
    tilhorighet: z.string().max(120).optional(),
    /** Kan attestere andres timer. */
    kanAttestere: z.boolean().default(false),
    opprettetTsMs: z.number().int(),
});
/** Trygg parse — feltdata er upålitelig, så aldri kast. */
export function parseOkt(raw) {
    const r = OktSchema.safeParse(raw);
    return r.success ? r.data : null;
}
export function parsePerson(raw) {
    const r = PersonSchema.safeParse(raw);
    return r.success ? r.data : null;
}
//# sourceMappingURL=typer.js.map